/**
 * ViewLogo — the Trusscore logo over a corner of the designer's view.
 *
 * Drawn on top of the 3D picture, not in it, so lighting, exposure and effects
 * never touch its colours. Which logo, which corner, how big and how solid all
 * come from the render settings (Effects → Logo).
 *
 * The designer keeps things of its own in three of the corners while a surface
 * is being edited; `clear` says how much room (px) to leave below or above the
 * corner's edge so the logo sits beside them rather than under them.
 */

import React from 'react';
import { LogoPosition, LogoVersion } from '../../ui/room3d/renderSettings';
import { useRenderSettings } from './useRenderSettings';

const base = process.env.PUBLIC_URL || '';

/** Each version's file, and its height in px at scale 1. */
const LOGOS: Record<LogoVersion, { src: string; height: number }> = {
  icon: { src: `${base}/images/trusscore-icon.svg`, height: 52 },
  light: { src: `${base}/images/trusscore-colour-light.svg`, height: 30 },
  dark: { src: `${base}/images/trusscore-colour-dark.svg`, height: 30 },
};

export interface ViewLogoProps {
  /** Extra space to leave at a corner's top or bottom edge, where something else already sits. */
  clear?: Partial<Record<LogoPosition, number>>;
}

export const ViewLogo: React.FC<ViewLogoProps> = ({ clear }) => {
  const settings = useRenderSettings();
  if (!settings.logo || settings.logoOpacity <= 0) return null;

  const logo = LOGOS[settings.logoVersion];
  const [vertical, horizontal] = settings.logoPosition.split('-') as ['top' | 'bottom', 'left' | 'right'];
  const margin = settings.logoMargin;

  return (
    <img
      src={logo.src}
      alt="Trusscore"
      draggable={false}
      style={{
        position: 'absolute',
        zIndex: 1,
        [vertical]: margin + (clear?.[settings.logoPosition] ?? 0),
        [horizontal]: margin,
        height: logo.height * settings.logoScale,
        width: 'auto',
        opacity: settings.logoOpacity,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    />
  );
};

export default ViewLogo;
